<template>
  <div class="inbound-dest-panel">
    <div class="box-title">
      <h4>
        <i class="fal fa-exchange-alt"></i>
        ຮັບເຂົ້າແລ້ວປ່ຽນປາຍທາງ (Inbound Change Destination)
      </h4>
    </div>
    <p class="inbound-dest-hint">
      ເມື່ອເປີດໃຊ້ງານ ແລະ ພັດສະດຸທີ່ມີ "ສາຂາປາຍທາງເດີມ" ຢູ່ໃນລາຍການຂ້າງລຸ່ມ ຖືກຍິງເຂົ້າ (inbound) ທີ່ສາຂານີ້
      ລະບົບຈະປ່ຽນສາຂາປາຍທາງຂອງບິນນັ້ນມາເປັນສາຂານີ້ ແລະ ຖືວ່າພັດສະດຸຮອດປາຍທາງແລ້ວ (ເຊັນຮັບໄດ້ເລີຍ).
      ສາຂາຕົ້ນທາງຂອງບິນຈະບໍ່ຖືກປ່ຽນ.
    </p>

    <div v-if="loadError" class="notification is-warning" :style="{ marginTop: '10px' }">
      <i class="fal fa-exclamation-triangle" :style="{ marginRight: '6px' }"></i>
      ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໄດ້ — ລະບົບຫຼັງບ້ານອາດຍັງບໍ່ຮອງຮັບ ຫຼື ເກີດຂໍ້ຜິດພາດ. ກະລຸນາລອງໃໝ່ພາຍຫຼັງ.
      <vs-button transparent :style="{ display: 'inline-block', marginLeft: '8px' }" @click="retryLoad()">
        ລອງໃໝ່
      </vs-button>
    </div>

    <div v-else-if="info.schema_ready === false" class="notification is-warning" :style="{ marginTop: '10px' }">
      <i class="fal fa-database" :style="{ marginRight: '6px' }"></i>
      ຖານຂໍ້ມູນຝັ່ງ backend ຍັງບໍ່ພ້ອມສຳລັບ feature ນີ້ (migration ຍັງບໍ່ໄດ້ດຳເນີນ) — ການຮັບເຂົ້າຍັງເຮັດວຽກຄືເກົ່າທຸກຢ່າງ.
    </div>

    <!-- The write landed but the shared cache could not be invalidated, so scans
         may keep using the previous configuration for up to 5 minutes.
         Shared component so this page, the DC-code tab and the returning-rule
         settings page cannot drift apart on what the warning means. -->
    <CacheStaleNotice :visible="cacheStale">ການຍິງຮັບເຂົ້າອາດຍັງໃຊ້ຄ່າເກົ່າ</CacheStaleNotice>

    <div class="columns is-multiline" :style="{ marginTop: '10px' }">
      <div class="column is-5">
        <div class="inbound-dest-card" :class="{ 'is-enabled': info.is_enabled }">
          <label class="label">ສະຖານະການໃຊ້ງານຂອງສາຂານີ້</label>
          <p class="inbound-dest-value">
            <i :class="info.is_enabled ? 'fal fa-check-circle' : 'fal fa-times-circle'"
               :style="{ marginRight: '6px' }"></i>
            {{ info.is_enabled ? 'ເປີດໃຊ້ງານ' : 'ປິດໄວ້' }}
          </p>
          <p class="inbound-dest-note">
            {{ info.branch_name || '-' }}
          </p>
          <!-- vs-button is `display: flex`, i.e. block-level, so on its own it
               stretches to the full card width. The flex wrapper makes it a flex
               ITEM so it shrinks to its label, and centres it in the card. -->
          <div class="inbound-dest-toggle">
            <vs-button
                :loading="toggleLoading"
                :disabled="!canMutate"
                :danger="info.is_enabled"
                @click="ToggleEnabled()">
              {{ info.is_enabled ? 'ປິດການປ່ຽນປາຍທາງ' : 'ເປີດການປ່ຽນປາຍທາງ' }}
            </vs-button>
          </div>
          <p v-if="info.is_enabled && activeTargets.length === 0" class="inbound-dest-warning">
            <i class="fal fa-exclamation-triangle" :style="{ marginRight: '4px' }"></i>
            ເປີດໃຊ້ງານແລ້ວ ແຕ່ຍັງບໍ່ມີສາຂາປາຍທາງເດີມ — ຈະຍັງບໍ່ມີຜົນຫຍັງເທື່ອ
          </p>
        </div>
      </div>
    </div>

    <div class="columns is-multiline">
      <div class="column is-5">
        <div class="field">
          <div class="control">
            <label class="label">
              ເພີ່ມສາຂາປາຍທາງເດີມ ທີ່ຈະຮັບແທນ
              <span class="text-danger">{{ server_errors.targetEndBranchId }}</span>
            </label>
            <model-list-select
                :list="branchOptionList"
                v-model="targetEndBranchId"
                name="targetEndBranchId"
                placeholder="ກະລຸນາເລືອກສາຂາປາຍທາງເດີມ..."
                option-text="label"
                option-value="id">
            </model-list-select>
          </div>
        </div>
      </div>
      <div class="column is-4 inbound-dest-actions">
        <vs-button :loading="btnLoading" :disabled="!targetEndBranchId || !canMutate" @click="AddTarget()">
          <i class="fal fa-plus-circle" :style="{ marginRight: '6px' }"></i>
          ເພີ່ມສາຂາປາຍທາງເດີມ
        </vs-button>
      </div>
    </div>

    <div class="center" :style="{ marginTop: '10px' }">
      <vs-table>
        <template #thead>
          <vs-tr>
            <vs-th>ລຳດັບ</vs-th>
            <vs-th>ສາຂາປາຍທາງເດີມ</vs-th>
            <vs-th>ສະຖານະ</vs-th>
            <vs-th>ອັບເດດຫຼ້າສຸດ</vs-th>
            <vs-th></vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr :key="index" v-for="(item, index) in info.targets">
            <vs-td>{{ index + 1 }}</vs-td>
            <vs-td>{{ item.target_branch_name || ('#' + item.target_end_branch_id) }}</vs-td>
            <vs-td>
              <span :class="item.is_active ? 'tag-active' : 'tag-inactive'">
                {{ item.is_active ? 'ໃຊ້ງານ' : 'ຍົກເລີກແລ້ວ' }}
              </span>
            </vs-td>
            <vs-td>{{ item.updated_at || '-' }}</vs-td>
            <vs-td>
              <vs-button
                  v-if="item.is_active && canMutate"
                  circle icon flat danger
                  @click="OpenRemove(item)">
                <i class="fad fa-trash-alt"></i>
              </vs-button>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
      <p v-if="info.targets.length === 0 && !isLoading" class="inbound-dest-empty">
        ຍັງບໍ່ມີສາຂາປາຍທາງເດີມທີ່ຕັ້ງໄວ້
      </p>
    </div>

    <Loading v-if="isLoading"/>

    <vs-dialog prevent-close v-model="removeModalActive">
      <div class="con-content" :style="{ marginTop: '15px' }">
        <p :style="{ fontSize: '18px', color: '#195BFF' }">
          ກະລຸນາກົດຢືນຢັນ ເພື່ອຍົກເລີກສາຂາປາຍທາງເດີມ
          <b>{{ removingTarget ? (removingTarget.target_branch_name || removingTarget.target_end_branch_id) : '' }}</b>
        </p>
      </div>
      <div class="con-footer" :style="{ marginTop: '25px' }">
        <vs-button
            :loading="removeLoading"
            transparent :style="{ color: 'crimson' }"
            @click="RemoveTarget()"
        >
          ຢືນຢັນ
        </vs-button>
        <vs-button @click="removeModalActive = false" dark transparent>
          ຍົກເລີກ
        </vs-button>
      </div>
    </vs-dialog>
  </div>
</template>
<script>
import { ModelListSelect } from 'vue-search-select';
import Loading from '@/views/Loading';
import CacheStaleNotice from '@/components/CacheStaleNotice';

export default {
  components: { ModelListSelect, Loading, CacheStaleNotice },

  data: () => ({
    isLoading: false,
    loadError: false,
    btnLoading: false,
    toggleLoading: false,
    removeLoading: false,
    removeModalActive: false,
    removingTarget: null,
    // Last mutation committed but its cache invalidation failed — scans can lag
    // behind the saved configuration until the backend TTL expires.
    cacheStale: false,
    targetEndBranchId: '',
    branches: [],
    info: {
      branch_id: null,
      branch_name: '',
      is_enabled: false,
      schema_ready: true,
      targets: [],
    },
    server_errors: {
      targetEndBranchId: '',
    },
  }),

  computed: {
    branchId() {
      return this.$route.params.id;
    },
    activeTargets() {
      return (this.info.targets || []).filter(item => item.is_active);
    },
    // Every mutation needs the backend schema in place; without it the API
    // answers 422 and the buttons would only produce error toasts.
    canMutate() {
      return !this.loadError && this.info.schema_ready !== false;
    },
    // The branch being edited can never take over itself, and a branch already
    // listed as an active target is not worth offering twice.
    branchOptionList() {
      const taken = this.activeTargets.map(item => String(item.target_end_branch_id));
      return this.branches
          .filter(item => String(item.id) !== String(this.branchId))
          .filter(item => taken.indexOf(String(item.id)) === -1)
          .map(item => ({
            id: item.id,
            label: `${item.province ? item.province.prefix + '-' : ''}${item.name}`,
          }));
    },
  },

  created() {
    this.fetchData();
    this.fetchBranches();
  },

  methods: {
    retryLoad() {
      this.loadError = false;
      this.fetchData();
      this.fetchBranches();
    },
    fetchData() {
      this.isLoading = true;
      /// A fresh load of the current server state is where a warning about a PAST save stops
      /// being useful — by then the cache has usually expired, and the page below shows the
      /// value actually in force.
      this.cacheStale = false;
      this.$axios.get('get-branch-inbound-destination-change/' + this.branchId).then(res => {
        this.info = res.data;
        this.isLoading = false;
      }).catch(() => {
        // Backend not deployed yet / outage: say so instead of rendering empty
        // values as if the branch were simply unconfigured.
        this.isLoading = false;
        this.loadError = true;
        this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
      });
    },
    fetchBranches() {
      this.$http.get('v1/listing/branches').then(res => {
        this.branches = res.data || [];
      }).catch(() => {
        this.loadError = true;
      });
    },
    ToggleEnabled() {
      this.toggleLoading = true;
      this.$axios.post('set-branch-inbound-destination-change/' + this.branchId, {
        isEnabled: !this.info.is_enabled,
      }).then(res => {
        this.toggleLoading = false;
        if (res.data.success == true) {
          this.info = res.data;
          this.cacheStale = res.data.cache_stale === true;
          this.$notification.OpenNotification_EditItem_OnSuccess('top-right', 'dark', 3000);
        }
      }).catch(error => {
        this.toggleLoading = false;
        this.notifyError(error);
      });
    },
    AddTarget() {
      if (!this.targetEndBranchId) {
        return;
      }
      this.server_errors.targetEndBranchId = '';
      this.btnLoading = true;
      this.$axios.post('add-branch-inbound-destination-target/' + this.branchId, {
        targetEndBranchId: this.targetEndBranchId,
      }).then(res => {
        this.btnLoading = false;
        if (res.data.success == true) {
          this.info = res.data;
          this.cacheStale = res.data.cache_stale === true;
          this.targetEndBranchId = '';
          this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000);
        }
      }).catch(error => {
        this.btnLoading = false;
        if (error.response && error.response.status === 422) {
          this.server_errors.targetEndBranchId = this.serverErrorMessage(error.response.data?.error);
        } else {
          this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
        }
      });
    },
    OpenRemove(item) {
      this.removingTarget = item;
      this.removeModalActive = true;
    },
    RemoveTarget() {
      if (!this.removingTarget) {
        return;
      }
      const targetId = this.removingTarget.target_end_branch_id;
      this.removeLoading = true;
      this.$axios.delete('delete-branch-inbound-destination-target/' + this.branchId + '/' + targetId).then(res => {
        this.removeLoading = false;
        this.removeModalActive = false;
        this.removingTarget = null;
        if (res.data.success == true) {
          this.info = res.data;
          this.cacheStale = res.data.cache_stale === true;
          this.$notification.OpenNotification_DeleteItem_OnSuccess('top-right', 'danger', 3000);
        } else {
          // e.g. NOT_FOUND (already removed elsewhere) — show the refreshed state
          // instead of pretending the delete just succeeded.
          this.info = res.data;
          this.$notification.openNotification(
            { title: 'ປ່ຽນປາຍທາງ', text: 'ບໍ່ມີລາຍການໃຫ້ຍົກເລີກແລ້ວ — ໂຫຼດຂໍ້ມູນຄືນໃໝ່' },
            'top-right', 'danger', 5000
          );
        }
      }).catch(error => {
        this.removeLoading = false;
        this.removeModalActive = false;
        this.removingTarget = null;
        this.notifyError(error);
      });
    },
    notifyError(error) {
      if (error.response && error.response.status === 422) {
        this.$notification.openNotification(
          { title: 'ປ່ຽນປາຍທາງ', text: this.serverErrorMessage(error.response.data?.error) },
          'top-right', 'danger', 5000
        );
      } else {
        this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
      }
    },
    serverErrorMessage(errorCode) {
      if (errorCode === 'SCHEMA_NOT_READY') {
        return 'ຖານຂໍ້ມູນຝັ່ງ backend ຍັງບໍ່ພ້ອມສຳລັບ feature ນີ້ (migration ຍັງບໍ່ໄດ້ດຳເນີນ)';
      }
      if (errorCode === 'BRANCH_NOT_FOUND') {
        return 'ບໍ່ພົບສາຂານີ້ໃນລະບົບ';
      }
      if (errorCode === 'TARGET_BRANCH_NOT_FOUND') {
        return 'ບໍ່ພົບສາຂາປາຍທາງເດີມທີ່ເລືອກ';
      }
      if (errorCode === 'TARGET_IS_SAME_BRANCH') {
        return 'ບໍ່ສາມາດເລືອກສາຂາຕົນເອງເປັນປາຍທາງເດີມໄດ້';
      }
      if (errorCode === 'TARGET_WOULD_CYCLE') {
        return 'ສາຂານີ້ຖືກຕັ້ງໃຫ້ຮັບແທນຢູ່ແລ້ວ ຈາກສາຂາທີ່ເລືອກ — ຈະເຮັດໃຫ້ປ່ຽນປາຍທາງວົນໄປມາ';
      }
      return 'ບໍ່ສາມາດບັນທຶກໄດ້ ກະລຸນາກວດຂໍ້ມູນທີ່ເລືອກຄືນ';
    },
  },
};
</script>
<style lang="scss" scoped>
.inbound-dest-panel {
  margin-top: 10px;

  .inbound-dest-hint {
    color: #666;
    font-size: 14px;
    margin-top: 6px;
  }

  .inbound-dest-card {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 14px 16px;

    &.is-enabled {
      border-color: #195BFF;
    }

    .inbound-dest-value {
      font-size: 20px;
      font-weight: 600;
      margin-top: 4px;
    }

    .inbound-dest-note {
      margin-top: 6px;
      font-size: 13px;
      color: #666;
    }

    .inbound-dest-warning {
      margin-top: 8px;
      font-size: 13px;
      color: #d97706;
    }

    .inbound-dest-toggle {
      display: flex;
      justify-content: center;
      margin-top: 10px;
    }
  }

  .inbound-dest-actions {
    display: flex;
    align-items: flex-end;
  }

  .inbound-dest-empty {
    color: #999;
    font-size: 14px;
    padding: 12px 0;
    text-align: center;
  }

  .tag-active {
    color: #195BFF;
    font-weight: 600;
  }

  .tag-inactive {
    color: #999;
  }
}
</style>
